import { useEffect, useRef, useState } from "react";

import { UserSubscriptionAction, SubscriptionCancelReason } from "common/types";
import { useNavigate } from "common/router/hooks";

import {
  OnboardingDetailSubscriptionStepParams,
  OnboardingSubscriptionsStep,
} from "client/onboarding/types";
import Button from "client/common/components/buttons/Button";
import { useUpdateSubscriptionMutation } from "client/subscriptions/hooks";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import { useInvalidateOnboarding } from "client/onboarding/hooks/invalidate";
import { urls } from "client/routes";
import TextArea from "client/common/components/inputs/TextArea";
import { onboardingSubscriptionCancelReasonOptions } from "client/onboarding/utils";
import Select from "client/common/components/inputs/Select";
import Tooltip from "client/common/components/content/Tooltip";

import StepLayout from "../OnboardingSubscriptionsStepLayout";
import OnboardingSubscriptionItem from "../OnboardingSubscriptionItem";

const OnboardingSubscriptionCancelStep = ({
  id,
  subscription,
  onNext,
  isLoadingNext,
  stepper,
}: OnboardingDetailSubscriptionStepParams) => {
  const navigate = useNavigate();
  const invalidateOnboarding = useInvalidateOnboarding();
  const {
    mutate: updateSubscription,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateSubscriptionMutation(id, {
    onSuccess: () => {
      invalidateOnboarding();
      onNext();
    },
  });

  const reasonOtherRef = useRef<HTMLTextAreaElement>(null);
  const [reason, setReason] = useState<SubscriptionCancelReason | "">("");
  const [reasonOther, setReasonOther] = useState<string>("");
  const isReasonOther = reason === SubscriptionCancelReason.Other;

  useEffect(() => {
    if (isReasonOther) {
      reasonOtherRef.current?.focus();
    } else {
      setReasonOther("");
    }
  }, [isReasonOther]);

  const canSubmit =
    !!reason && (!isReasonOther || !!reasonOther.trim()) && !isUpdating && !isLoadingNext;

  const getSubmitTooltipContent = () => {
    if (!reason) {
      return "Please let us know why you're canceling your autoship before proceeding.";
    }
    if (isReasonOther && !reasonOther.trim()) {
      return "Please specify a reason before proceeding.";
    }
    return undefined;
  };

  return (
    <StepLayout stepper={stepper}>
      {!!updateError && (
        <FetchErrorAlert error={updateError} fieldErrors={[updateError.errors.action]} />
      )}
      <StepLayout.Title>Are you sure you want to cancel?</StepLayout.Title>
      <StepLayout.Description>
        By canceling your Autoship, you will no longer receive your scheduled home delivery orders
        or the Autoship discount.
      </StepLayout.Description>
      <OnboardingSubscriptionItem subscription={subscription}>
        <OnboardingSubscriptionItem.Pricing subscription={subscription} />
      </OnboardingSubscriptionItem>
      <Select
        label="Let us know why you're canceling..."
        value={reason}
        onChange={(r) => setReason(r as SubscriptionCancelReason | "")}
        disabled={isUpdating}
      >
        {onboardingSubscriptionCancelReasonOptions().map(({ label, value }) => (
          <Select.Option key={value} value={value}>
            {label}
          </Select.Option>
        ))}
      </Select>
      {isReasonOther && (
        <TextArea
          ref={reasonOtherRef}
          label="I am canceling this Autoship because..."
          value={reasonOther || ""}
          onChange={setReasonOther}
          disabled={isUpdating}
        />
      )}
      <StepLayout.Footer>
        <Tooltip isEnabled={!canSubmit} content={getSubmitTooltipContent()} position="below">
          <Button
            onClick={() =>
              updateSubscription({
                action: UserSubscriptionAction.Cancel,
                cancel_reason: reason || undefined,
                cancel_reason_other: reasonOther,
              })
            }
            isLoading={isUpdating || isLoadingNext}
            disabled={!canSubmit}
          >
            Yes, I want to cancel my Autoship
          </Button>
        </Tooltip>
        <Button
          onClick={() =>
            navigate(
              urls.onboarding.subscriptions.detail({
                params: {
                  id,
                  step: OnboardingSubscriptionsStep.QuantityFrequency,
                },
              }),
            )
          }
          variant="secondary"
          disabled={isUpdating}
        >
          Never mind! Update my Autoship!
        </Button>
      </StepLayout.Footer>
    </StepLayout>
  );
};

export default OnboardingSubscriptionCancelStep;
